/**
 * Voice In — speech-to-text dictation
 *
 * Declares a single web-accessible-resource entry — `images/*`, `css/*.css`,
 * three named HTML pages and `audio/*.wav` — matched to every origin with no
 * `use_dynamic_url`. All are reachable from any page at a fixed URL.
 *
 * The per-site icons are the cleanest signal: `images/icon/gmail.png` and friends are
 * vendor-bundled images that load through `probeImage` with no CORS grant, so they
 * survive a restrictive page CSP. The named CSS and settings page corroborate.
 *
 * Manifest verified against version 4.44.
 */

import { probeImage, probeFetch } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'pjnefijmagpdjfhhkpljicbbpicelgko';

export const voiceIn: ExtensionDefinition = {
  id: ID,
  name: 'Voice In - Speech-To-Text Dictation',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 600_000,
  notes:
    'Exposes images/, css/ and named HTML pages to every origin with no dynamic URL. ' +
    'The bundled icon images are conclusive and need no CORS grant.',
  signals: [
    {
      id: 'war-gmail-icon-image',
      kind: 'web-accessible-resource',
      description:
        'Load images/icon/gmail.png via <img> — one of the bundled per-site icons ' +
        'exposed to every origin; needs no CORS grant, so it works under a strict CSP.',
      weight: 1,
      run: () => probeImage(ID, 'images/icon/gmail.png'),
    },
    {
      id: 'war-page-css',
      kind: 'web-accessible-resource',
      description:
        'Fetch css/page.css — a vendor stylesheet exposed to every origin, an ' +
        'independent non-image path.',
      weight: 0.5,
      run: () => probeFetch(ID, 'css/page.css'),
    },
    {
      id: 'war-settings-html',
      kind: 'web-accessible-resource',
      description:
        'Fetch settings.html — the named settings page, a distinctive third path.',
      weight: 0.5,
      run: () => probeFetch(ID, 'settings.html'),
    },
  ],
};
